import { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { COUNTRIES } from "@/data/countries";

/**
 * Searchable dial-code picker.
 *
 * Props:
 *   value       — the currently selected dial code string, e.g. "+39"
 *   onChange    — callback(dialCode: string)
 *   className   — optional extra classes on the trigger button
 *   testId      — data-testid for the trigger
 */
export function DialCodePicker({ value, onChange, className, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso.toLowerCase().includes(q) ||
        c.code.includes(q)
    );
  }, [search]);

  // Display label on the trigger — show ISO + code for compactness
  const selected = COUNTRIES.find((c) => c.code === value);
  const triggerLabel = selected ? `${selected.iso} ${selected.code}` : value ?? "Code";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-36 justify-between font-normal", className)}
          data-testid={testId}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search country or code…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && (
              <CommandEmpty>No country found.</CommandEmpty>
            )}
            {filtered.map((c) => (
              <CommandItem
                key={c.iso}
                value={c.iso}
                onSelect={() => {
                  onChange(c.code);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === c.code ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="font-mono mr-2 text-slate-500 w-10">{c.code}</span>
                <span>{c.name}</span>
                <span className="ml-auto text-xs text-slate-400">{c.iso}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
